import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './staffLevelDialog.module.scss'
import { useMutation } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import UserInput from '@/components/_custom/UserInput/userInput'
import { Daycare } from '@/types/Daycare'
import { StaffLevel } from '@/types/StaffLevel'
import { CREATE_STAFF_LEVEL, UPDATE_STAFF_LEVEL } from '@/modules/API/staff/staffLevels'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface staffLevelDialogProps {
    open: boolean
    handleClose: () => void
    daycare: Daycare
    create: boolean
    // CREATE
    addOneStaffLevel?: (value: StaffLevel) => void
    // EDIT
    staffLevelId?: number
    staffLevelName?: string
    setStaffLevelName?: (value: string) => void
    staffLevelRequired?: boolean
    setStaffLevelRequired?: (value: boolean) => void
    updateOneStaffLevel?: (value: StaffLevel) => void
}

const StaffLevelDialog: FunctionComponent<staffLevelDialogProps> = ({
    open,
    handleClose,
    daycare,
    create,
    addOneStaffLevel,
    staffLevelId,
    staffLevelName,
    setStaffLevelName,
    staffLevelRequired,
    setStaffLevelRequired,
    updateOneStaffLevel
}: staffLevelDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newGroupName, setNewGroupName] = useState<string>(staffLevelName || '')
    const [newStaffLevelRequired, setNewStaffLevelRequired] = useState<boolean>(staffLevelRequired || false)

    const [createStaffLevel] = useMutation(CREATE_STAFF_LEVEL, {
        variables: {
            daycareId: daycare.id,
            name: newGroupName,
            required: newStaffLevelRequired
        },
        onCompleted: ({ createStaffLevel }) => {
            if (addOneStaffLevel)
                addOneStaffLevel({
                    id: createStaffLevel,
                    daycareId: daycare.id,
                    name: newGroupName,
                    required: newStaffLevelRequired
                })

            setNewGroupName('')
            setNewStaffLevelRequired(false)

            handleClose()

            showNotificationSuccess(t('staffLevelAdded'))
        },
        onError: () => {
            showNotificationError(t('errorAddedStaffLevel'))
        }
    })

    const [updateStaffLevel] = useMutation(UPDATE_STAFF_LEVEL, {
        variables: {
            staffLevelId: Number(staffLevelId) || 0,
            name: newGroupName,
            daycareId: Number(daycare.id) || 0,
            required: newStaffLevelRequired
        },
        onCompleted: () => {
            if (updateOneStaffLevel && staffLevelId)
                updateOneStaffLevel({
                    id: staffLevelId,
                    daycareId: daycare.id,
                    name: newGroupName,
                    required: newStaffLevelRequired
                })

            setNewGroupName('')

            if (setStaffLevelName) setStaffLevelName(newGroupName)
            if (setStaffLevelRequired) setStaffLevelRequired(newStaffLevelRequired)

            handleClose()

            showNotificationSuccess(t('staffLevelUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdatedStaffLevel'))
        }
    })

    const disabled: boolean = create
        ? newGroupName === ''
        : staffLevelName === newGroupName && staffLevelRequired === newStaffLevelRequired

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={create ? t('addStaffLevel') : t('updateStaffLevel')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.Input}>
                        <UserInput
                            label={t('name')}
                            value={newGroupName}
                            type='text'
                            required
                            fullWidth
                            format={'pascal'}
                            className={styles.UserInput}
                            unallowedCharacters={'special'}
                            setValue={setNewGroupName}
                        />
                        <div className={styles.CheckBox}>
                            <Checkbox
                                checked={newStaffLevelRequired}
                                onChange={() => setNewStaffLevelRequired(!newStaffLevelRequired)}
                                text={t('required')}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={create ? 'add' : 'update'}
                        onClick={create ? createStaffLevel : updateStaffLevel}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default StaffLevelDialog
